import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import {
  Search,
  TrendingUp,
  BarChart3,
  CandlestickChart,
  Building2,
  Sparkles,
  Activity,
  Wallet,
  RefreshCcw,
  Scale,
  BookOpen,
  Eye,
  Layers,
  ShoppingBag,
  Code,
  Settings,
  Command as _Command
} from 'lucide-react'

const NAV_ITEMS = [
  { name: 'Dashboard', href: '/', icon: BarChart3, keywords: 'home overview' },
  {
    name: 'Algorithms',
    href: '/algorithms',
    icon: TrendingUp,
    keywords: 'algo strategy bot'
  },
  {
    name: 'Charts',
    href: '/charts',
    icon: CandlestickChart,
    keywords: 'candle graph'
  },
  {
    name: 'Brokers',
    href: '/brokers',
    icon: Building2,
    keywords: 'connect angelone zerodha'
  },
  {
    name: 'Market Monitor',
    href: '/market-monitor',
    icon: Sparkles,
    keywords: 'artificial intelligence'
  },
  {
    name: 'Trading History',
    href: '/trading-history',
    icon: Activity,
    keywords: 'trades past'
  },
  {
    name: 'Paper Trading',
    href: '/paper-trading',
    icon: Wallet,
    keywords: 'simulate virtual'
  },
  {
    name: 'Contract Rollover',
    href: '/contract-rollover',
    icon: RefreshCcw,
    keywords: 'expiry'
  },
  { name: 'Arbitrage', href: '/arbitrage', icon: Scale, keywords: 'spread' },
  {
    name: 'Trade Journal',
    href: '/journal',
    icon: BookOpen,
    keywords: 'notes diary'
  },
  {
    name: 'Screener',
    href: '/screener',
    icon: Search,
    keywords: 'filter scan stock'
  },
  {
    name: 'OI Analysis',
    href: '/oi-analysis',
    icon: Eye,
    keywords: 'open interest pcr'
  },
  {
    name: 'Options Builder',
    href: '/options',
    icon: Layers,
    keywords: 'strategy greeks'
  },
  {
    name: 'Marketplace',
    href: '/marketplace',
    icon: ShoppingBag,
    keywords: 'browse subscribe'
  },
  {
    name: 'Code Editor',
    href: '/code-editor',
    icon: Code,
    keywords: 'script custom'
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings,
    keywords: 'preferences config'
  }
]

function fuzzyMatch(text, query) {
  const lower = text.toLowerCase()
  const q = query.toLowerCase()
  return lower.includes(q)
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()
  const disabled = location.pathname.startsWith('/admin')

  const filtered = useMemo(() => {
    if (!query) return NAV_ITEMS
    return NAV_ITEMS.filter(
      item => fuzzyMatch(item.name, query) || fuzzyMatch(item.keywords, query)
    )
  }, [query])

  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
    setSelectedIndex(0)
  }, [])

  const selectItem = useCallback(
    item => {
      navigate(item.href)
      close()
    },
    [navigate, close]
  )

  // Global keyboard shortcut
  useEffect(() => {
    if (disabled) return undefined
    const handleKeyDown = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(prev => !prev)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [disabled])

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Keyboard navigation inside palette
  const handleKeyDown = e => {
    if (e.key === 'Escape') {
      close()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      selectItem(filtered[selectedIndex])
    }
  }

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const el = listRef.current.children[selectedIndex]
      if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  if (!open || disabled) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[20vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      {/* Palette */}
      <div
        className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center px-4 border-b border-gray-200 dark:border-gray-700">
          <Search
            className="w-5 h-5 text-gray-400 shrink-0"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search pages..."
            className="w-full px-3 py-3 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none text-sm"
            aria-label="Search pages"
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-list"
            aria-activedescendant={
              filtered[selectedIndex] ? `cmd-item-${selectedIndex}` : undefined
            }
          />

          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 rounded font-mono">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <ul
          id="command-palette-list"
          ref={listRef}
          role="listbox"
          className="max-h-72 overflow-y-auto py-2"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-gray-500">
              No results found
            </li>
          ) : (
            filtered.map((item, index) => {
              const Icon = item.icon
              const isSelected = index === selectedIndex
              return (
                <li
                  key={item.href}
                  id={`cmd-item-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => selectItem(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`flex items-center px-4 py-2 cursor-pointer text-sm ${
                    isSelected
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-3 shrink-0" aria-hidden="true" />
                  <span>{item.name}</span>
                </li>
              )
            })
          )}
        </ul>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 flex items-center gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded font-mono">
              ↑↓
            </kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded font-mono">
              ↵
            </kbd>
            select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded font-mono">
              esc
            </kbd>
            close
          </span>
        </div>
      </div>
    </div>,
    document.body
  )
}
